import Vivus from "vivus";
import { scheduleNext } from "./timing";

// Draw animation using Vivus.js with simultaneous (configurable) fill fade-in.
// loopBehavior: once | infinite | sporadic

const FILL_FADE_START_PROGRESS = 0.35;
const FILL_FADE_REMAINING_FRACTION = 1;
const HOLD_AFTER_COMPLETE_MS = 300;
const POST_FADE_EXTRA_HOLD_FACTOR = 0.25;

const mapVivusEase = (ease) => {
	if (!ease) return Vivus.EASE;
	switch (ease) {
		case "linear":
			return Vivus.LINEAR;
		case "ease":
			return Vivus.EASE;
		case "elastic":
			return Vivus.EASE_OUT;
		default:
			return Vivus.EASE;
	}
};

const ensureElementId = (el) => {
	if (!el.id) el.id = "svg-anim-" + Math.random().toString(36).slice(2, 11);
	return el.id;
};

const collectTargets = (root) =>
	Array.from(root.querySelectorAll("path, circle, rect, polygon, line, polyline, ellipse")).filter(
		(el) => !el.closest("mask")
	);

const prepareStrokeMode = (root) => {
	if (!root) return { restore: () => {}, backups: [], fillTargets: [] };
	const targets = collectTargets(root);
	const backups = [];
	const fillTargets = [];

	targets.forEach((el) => {
		const cs = window.getComputedStyle(el);
		const orig = {
			el,
			fill: el.getAttribute("fill"),
			stroke: el.getAttribute("stroke"),
			fillOpacity: el.getAttribute("fill-opacity"),
			strokeWidth: el.getAttribute("stroke-width"),
		};
		backups.push(orig);

		const hasStroke = (cs.stroke && cs.stroke !== "none") || (orig.stroke && orig.stroke !== "none");
		if (!hasStroke) {
			const fillColor =
				(orig.fill && orig.fill !== "none") || (cs.fill && cs.fill !== "none") ? orig.fill || cs.fill : "#000";
			el.setAttribute("stroke", fillColor);
		}

		// Hide fill (both attribute + style) so we can fade it independently of Vivus stroke
		if ((cs.fill && cs.fill !== "none") || (orig.fill && orig.fill !== "none")) {
			el.setAttribute("fill-opacity", "0");
			try {
				el.style.fillOpacity = 0;
			} catch (e) {}
			fillTargets.push(el);
		}
		if (!el.getAttribute("stroke-width")) el.setAttribute("stroke-width", "2");
	});

	const restore = (restoreFill = true) => {
		backups.forEach(({ el, fill, stroke, fillOpacity, strokeWidth }) => {
			if (restoreFill) {
				if (fill !== null) el.setAttribute("fill", fill);
				else el.removeAttribute("fill");
				if (fillOpacity !== null) el.setAttribute("fill-opacity", fillOpacity);
				else el.removeAttribute("fill-opacity");
				try {
					if (fillOpacity !== null) el.style.fillOpacity = fillOpacity;
					else el.style.removeProperty("fill-opacity");
				} catch (e) {}
			}
			if (stroke !== null) el.setAttribute("stroke", stroke);
			else el.removeAttribute("stroke");
			if (strokeWidth !== null) el.setAttribute("stroke-width", strokeWidth);
		});
	};

	return { restore, backups, fillTargets };
};

const startFillFade = ({
	fillTargets,
	gsap,
	totalMs,
	fadeStartProgress = FILL_FADE_START_PROGRESS,
	remainingFraction = FILL_FADE_REMAINING_FRACTION,
	prevTweensRef,
}) => {
	if (!fillTargets.length) return null;
	const g = gsap || window.gsap;
	const fadeStart = Math.max(0, totalMs * fadeStartProgress);
	// Remaining time this fade should occupy (no artificial minimum so starting later really speeds it up)
	const remainingMs = Math.max(1, (totalMs - fadeStart) * remainingFraction);
	const durationSec = remainingMs / 1000;

	const doFade = () => {
		if (g) {
			// Kill any residual tweens from prior cycle
			if (prevTweensRef && prevTweensRef.current) {
				g.killTweensOf(prevTweensRef.current);
			}
			fillTargets.forEach((el) => {
				const apply = (v) => {
					el.setAttribute("fill-opacity", String(v));
					try {
						el.style.fillOpacity = v;
					} catch (e) {}
				};
				g.fromTo(
					el,
					{ attr: { "fill-opacity": 0 }, fillOpacity: 0 },
					{
						duration: durationSec,
						attr: { "fill-opacity": 1 },
						fillOpacity: 1,
						ease: "power1.out",
						onUpdate: () => {
							const cur = el.getAttribute("fill-opacity");
							if (cur != null) apply(cur);
						},
						onComplete: () => apply(1),
					}
				);
			});
			if (prevTweensRef) prevTweensRef.current = fillTargets;
		} else {
			fillTargets.forEach((el) => {
				const start = performance.now();
				const apply = (v) => {
					el.setAttribute("fill-opacity", String(v));
					try {
						el.style.fillOpacity = v;
					} catch (e) {}
				};
				const run = (now) => {
					const t = Math.min(1, (now - start) / remainingMs);
					apply(t);
					if (t < 1) requestAnimationFrame(run);
				};
				requestAnimationFrame(run);
			});
		}
	};

	const timeout = setTimeout(doFade, fadeStart);
	return () => clearTimeout(timeout);
};

const cleanupStrokeStyles = (el) => {
	if (!el) return;
	try {
		collectTargets(el).forEach((p) => {
			p.style.strokeDasharray = "";
			p.style.strokeDashoffset = "";
		});
	} catch (e) {}
};

let prevFadeTargetsRef = { current: null };

export const drawAnim = (
	element,
	{
		gsap,
		loopBehavior = "infinite",
		animatedElements,
		sporadicTimers,
		duration = 2,
		ease = "ease",
		type = "delayed",
		randomDelayMin = 3000,
		randomDelayMax = 8000,
	} = {}
) => {
	if (!element || !animatedElements) return null;
	if (animatedElements.has(element)) return animatedElements.get(element);

	const vivusEase = mapVivusEase(ease);
	const frames = Math.max(10, Math.round(duration * 60));
	let stopped = false;
	let cancelFade = null;
	let restartTimeout = null;
	let instance = null;

	const startSingleCycle = () => {
		// Kill any in-progress fade on previous targets
		if (gsap && prevFadeTargetsRef.current) {
			gsap.killTweensOf(prevFadeTargetsRef.current);
		}
		if (cancelFade) {
			cancelFade();
			cancelFade = null;
		}
		if (restartTimeout) {
			clearTimeout(restartTimeout);
			restartTimeout = null;
		}

		const prep = prepareStrokeMode(element);
		const { restore, fillTargets } = prep;

		// Create/re-init Vivus instance fresh each cycle
		const id = ensureElementId(element);
		const totalMs = (frames / 60) * 1000;

		cancelFade = startFillFade({
			fillTargets,
			gsap,
			totalMs,
			prevTweensRef: prevFadeTargetsRef,
		});

		instance = new Vivus(id, { start: "manual", type, duration: frames, animTimingFunction: vivusEase }, () => {
			if (stopped) return;
			if (!animatedElements.has(element)) return;
			if (loopBehavior === "infinite") {
				// Extra pause based on fade segment length
				const fadeSegmentMs = (frames / 60) * 1000 * (1 - FILL_FADE_START_PROGRESS);
				const extraPause = Math.round(fadeSegmentMs * POST_FADE_EXTRA_HOLD_FACTOR);
				restartTimeout = setTimeout(() => {
					if (stopped) return;
					startSingleCycle();
				}, HOLD_AFTER_COMPLETE_MS + extraPause);
			} else if (loopBehavior === "sporadic") {
				animatedElements.set(element, null);
				scheduleNext({
					element,
					animatedElements,
					sporadicTimers,
					startSingleCycle,
					randomDelayMin,
					randomDelayMax,
				});
			}
		});
		instance.play();

		const wrapper = {
			_vivus: instance,
			kill: () => {
				stopped = true;
				try {
					instance.stop();
				} catch (e) {}
				if (gsap && prevFadeTargetsRef.current) gsap.killTweensOf(prevFadeTargetsRef.current);
				if (cancelFade) cancelFade();
				if (restartTimeout) clearTimeout(restartTimeout);
				restore(true);
				cleanupStrokeStyles(element);
			},
		};
		animatedElements.set(element, wrapper);
		return wrapper;
	};

	return startSingleCycle();
};

export const drawPreview = (
	element,
	{ duration = 2, ease = "ease", type = "delayed", fadeStartProgress = FILL_FADE_START_PROGRESS } = {}
) => {
	if (!element) return null;
	const prep = prepareStrokeMode(element);
	const { restore, fillTargets } = prep;
	const frames = Math.max(10, Math.round(duration * 60));
	const vivusEase = mapVivusEase(ease);
	const id = ensureElementId(element);
	const totalMs = (frames / 60) * 1000;

	if (window.gsap && prevFadeTargetsRef.current) {
		window.gsap.killTweensOf(prevFadeTargetsRef.current);
	}

	const cancelFade = startFillFade({
		fillTargets,
		gsap: window.gsap,
		totalMs,
		fadeStartProgress,
		prevTweensRef: prevFadeTargetsRef,
	});

	let instance = new Vivus(id, { start: "manual", type, duration: frames, animTimingFunction: vivusEase }, () => {});
	instance.play();

	return {
		_vivus: instance,
		kill: () => {
			try {
				instance.stop();
			} catch (e) {}
			if (window.gsap && prevFadeTargetsRef.current) window.gsap.killTweensOf(prevFadeTargetsRef.current);
			if (cancelFade) cancelFade();
			restore(true);
			cleanupStrokeStyles(element);
		},
	};
};
// stop handled by shared stopManagedAnimation
